import { useQueryClient } from '@tanstack/vue-query';
import type { RouteLocationNormalized } from 'vue-router';

import { profileQueryOptions } from '@/entities/profile';
import { projectKeys, projectQueryOptions } from '@/entities/project';
import { teamQueryOptions } from '@/entities/team';
import { IS_BILLING_ENABLED } from '@/shared/config';
import { useTeamCookie } from '@/shared/hooks/use-team-cookie';

import { isFeatureAvailable } from './feature-guard';

const BILLING_BYPASS_ROUTES = new Set([
  'team/new',
  'team/billing',
  'team/invite',
  'access-restricted',
  'team',
  'team/settings',
]);

export const beforeEach = async (to: RouteLocationNormalized) => {
  const queryClient = useQueryClient();

  try {
    const isProfileNeeded = to.matched.some((record) => record.meta.profile);

    if (isProfileNeeded) {
      const profile = await queryClient.ensureQueryData(profileQueryOptions());

      if (!isFeatureAvailable(profile, to)) {
        return { name: '404' };
      }

      if (IS_BILLING_ENABLED && !BILLING_BYPASS_ROUTES.has(to.name as string)) {
        const { currentTeamUuid } = useTeamCookie();

        if (currentTeamUuid.value) {
          const team = await queryClient.ensureQueryData(teamQueryOptions(currentTeamUuid.value));

          if (!team?.is_billing_ready) {
            const isOwner = team.members.some(
              (m) => m.uuid === profile.uuid && m.role === 'Owner',
            );

            return { name: isOwner ? 'team/billing' : 'access-restricted' };
          }
        }
      }

      // Redirect to the default project if the user is trying to access the default project
      if (to.params.project_uuid === 'default') {
        const project = await queryClient.ensureQueryData(projectQueryOptions());

        queryClient.setQueryData(projectKeys.project(), project);

        return { name: 'project', params: { project_uuid: project.uuid } };
      }
    }
  } catch {
    queryClient.clear();
    return { name: 'login' };
  }
};
